import { BounceContainer } from '@components/BounceContainer/BounceContainer'
import { BTCNFT, NFT } from '@plug-core/interfaces/common'
import { logger } from '@plug-core/logger'
import { Spinner, cn } from '@ui'
import React, { FC, useEffect, useState } from 'react'

interface NFTDisplayerProps {
  nft: {
    url: NFT['id']
    contentType?: BTCNFT['contentType']
  }
  /** Extra classes for the media frame (e.g. a different radius). */
  className?: string
}

const TYPE_MAP: Record<string, keyof JSX.IntrinsicElements> = {
  'video/mp4': 'video',
  'image/png': 'img',
  'image/jpeg': 'img',
  'image/jpg': 'img',
  'image/svg+xml': 'img',
  'image/webp': 'img',
  'image/gif': 'img',
  'text/html': 'iframe',
  'text/html;charset=utf-8': 'iframe',
  'application/pdf': 'iframe',
}

const TAG_PROPS: Record<string, any> = {
  img: {
    width: '100%',
    height: '100%',
    className: 'block size-full object-contain',
  },
  iframe: {
    width: '100%',
    height: '100%',
    loading: 'lazy',
  },
  video: {
    autoPlay: true,
    muted: true,
    loop: true,
    width: '100%',
    height: '100%',
    className: 'block size-full object-contain',
  },
}

const NFTDisplayer: FC<NFTDisplayerProps> = ({ nft, className }) => {
  const [loading, setLoading] = useState(true)
  const [type, setType] = useState('image/png')

  useEffect(() => {
    if (!nft.contentType && nft.url) {
      fetch(nft.url)
        .then((response) => {
          setType(response.headers.get('Content-Type') || '')
        })
        .catch((e) => {
          logger.error('Failed to fetch NFT type', { originalError: e })
        })
    }
  }, [nft, nft.contentType])

  const tagFromType = TYPE_MAP[nft.contentType || type]
  if (!tagFromType) {
    logger.debug(`Unknown MIME type: ${nft.contentType || type}, falling back to img`)
  }

  const Tag = tagFromType || 'img'
  const customProps = TAG_PROPS[Tag] || {}

  if (Tag === 'iframe') {
    return (
      <div className="relative">
        <Tag
          {...customProps}
          className={cn('block size-full rounded-lg border-0', className)}
          src={nft.url}
        />
        {/* Swallows pointer events so a click selects the NFT instead of the framed page. */}
        <div className="absolute inset-0 size-full" />
      </div>
    )
  }

  return (
    <BounceContainer>
      <div
        className={cn(
          'relative aspect-square flex-1 overflow-hidden rounded-lg bg-surface-2',
          className
        )}
      >
        <Tag
          {...customProps}
          onLoad={() => setLoading(false)}
          onPlay={() => setLoading(false)}
          src={nft.url}
        />
        {loading && (
          <div className="absolute inset-0 z-[1] grid place-items-center bg-surface-2 text-fg-3">
            <Spinner size={28} />
          </div>
        )}
      </div>
    </BounceContainer>
  )
}

export default NFTDisplayer
